import { Box, Button, Card, CardContent, Stack, Typography } from "@mui/material";
import { useAppThemeSwitchContext } from "src/providers/ThemeProvider";

/**
 * Test page for iframe theme synchronization.
 *
 * This page demonstrates cross-iframe theme communication by embedding
 * the app in an iframe and showing that theme changes propagate.
 */
export function IframeTestPage() {
  const { mode, setPaletteMode } = useAppThemeSwitchContext();

  const toggleTheme = () => {
    setPaletteMode(mode === "light" ? "dark" : "light");
  };

  // Get the current page URL but point to home
  const iframeUrl = `${window.location.origin}${window.APP_CONFIG?.basePath || "/"}`;

  return (
    <Stack spacing={3} sx={{ p: 3 }}>
      <Typography variant="h4">Iframe Theme Sync Test</Typography>

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6">Parent Frame Controls</Typography>
            <Typography variant="body2" color="text.secondary">
              Current theme: <strong>{mode}</strong>
            </Typography>
            <Button variant="contained" onClick={toggleTheme} sx={{ maxWidth: 200 }}>
              Toggle Parent Theme
            </Button>
            <Typography variant="body2" color="text.secondary">
              Click the button above to change the parent theme. The iframe below should
              automatically sync to match.
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6">Embedded Iframe</Typography>
            <Typography variant="body2" color="text.secondary">
              The app is embedded below. When you toggle the theme above, the iframe should update
              immediately.
            </Typography>
            <Box
              sx={{
                width: "100%",
                height: 500,
                border: "2px solid",
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <iframe
                src={iframeUrl}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
                title="Theme sync test iframe"
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6">How It Works</Typography>
            <Typography variant="body2" component="div">
              <ol>
                <li>
                  <strong>On iframe load:</strong> The iframe sends a <code>theme-request</code>{" "}
                  message to the parent
                </li>
                <li>
                  <strong>Parent responds:</strong> Sends <code>theme-response</code> with current
                  theme
                </li>
                <li>
                  <strong>On theme change:</strong> Parent broadcasts <code>theme-change</code> to
                  all iframes
                </li>
                <li>
                  <strong>Iframe updates:</strong> Applies the new theme immediately
                </li>
              </ol>
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
